import { Alert, AlertDescription } from '@askable/ui/core/alert';
import { Button } from '@askable/ui/core/button';
import { Input } from '@askable/ui/core/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@askable/ui/core/table';
import { cn } from '@askable/ui/lib/utils';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import { useConnectedClient } from 'context/ConnectedClientContext';

import { InviteToTeam } from './data/InviteToTeam.mutation';

import type { FieldValues } from 'react-hook-form';

type Props = {
  title?: string | null;
  className?: string;
  showCloseIcon?: boolean;
  onDone?: () => void;
  onClose?: () => void;
};

type Invitation = {
  email: string;
  name: string;
};

type InvitationsFormValues = FieldValues & {
  invitations: Invitation[];
};

function InviteTeammate(props: Props) {
  const defaultInvitationFields = {
    email: '',
    name: '',
  };

  const clientDetails = useConnectedClient();

  const [, inviteToJoinTeam] = useMutation(InviteToTeam);

  const [sentInvites, setSentInvites] = useState<Invitation[]>([]);
  const [failedInvites, setFailedInvites] = useState<Array<{ invitation: Invitation; error: string }>>([]);

  const { control, register, handleSubmit, formState } = useForm<InvitationsFormValues>({
    defaultValues: {
      invitations: [defaultInvitationFields, defaultInvitationFields, defaultInvitationFields],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'invitations',
  });

  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const onSubmit = async (formValues: InvitationsFormValues) => {
    setLoading(true);
    const finalInvitations = formValues.invitations.filter(invite => !!invite.email);

    const results = await Promise.all(
      finalInvitations.map(invitation => {
        return inviteToJoinTeam({
          _id: clientDetails?.details?.team?.id!,
          email: invitation.email,
          name: invitation.name,
        }).then(result => {
          return {
            invitation,
            error: result.error,
          };
        });
      }),
    );

    // Separate successful and failed invitations
    const successful = results.filter(result => !result.error).map(result => result.invitation);
    const failed = results
      .filter(result => !!result.error)
      .map(result => ({
        invitation: result.invitation,
        error: result.error!.message,
      }));

    setSentInvites(successful);
    setFailedInvites(failed);

    setLoading(false);
    setShowConfirmation(true);
  };

  const renderForm = () => {
    return (
      <div className="my-6 grid w-full grid-cols-[1fr_1fr_auto] gap-4">
        <div>
          <h3 className="text-base font-semibold">Email address</h3>
        </div>
        <div>
          <h3 className="text-base font-semibold">Full name (optional)</h3>
        </div>
        <div /> {/* Filler component */}
        {fields.map((field, index) => {
          const error = formState.errors.invitations?.[index];

          return (
            <>
              <div key={field.id}>
                <Input
                  type="email"
                  required={index === 0}
                  {...register(`invitations.${index}.email`, { required: index === 0 })}
                />
                {error?.email?.message ? <div className="text-xs text-destructive">{error?.email?.message}</div> : null}
              </div>
              <div>
                <Input type="text" {...register(`invitations.${index}.name`)} />
              </div>
              <div className="self-end justify-self-center">
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (index === 0) {
                      return;
                    }
                    remove(index);
                  }}
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Remove</span>
                </Button>
              </div>
            </>
          );
        })}
        <div>
          <Button
            variant="link"
            onClick={() => {
              append(defaultInvitationFields);
            }}
          >
            + Add another
          </Button>
        </div>
      </div>
    );
  };

  const renderInvitationForm = () => {
    return (
      <form
        // eslint-disable-next-line @typescript-eslint/no-misused-promises -- react-hook-form handleSubmit returns a promise; the form ignores it
        onSubmit={handleSubmit(onSubmit)}
        className="pb-2"
      >
        {props.title !== null && (
          <h1>
            Invite people to <strong>{clientDetails?.details?.team?.name}</strong>
          </h1>
        )}
        {renderForm()}
        <div className="flex justify-end">
          <Button type="submit" isLoading={loading}>
            Send invitations
          </Button>
        </div>
      </form>
    );
  };

  const renderConfirmationMessage = () => {
    const totalInvites = sentInvites.length + failedInvites.length;
    const hasFailures = failedInvites.length > 0;
    const hasSuccesses = sentInvites.length > 0;

    return (
      <div className="pb-2">
        {/* Summary Alert */}
        <Alert variant={hasFailures ? 'destructive' : 'success'} className="mb-4 max-w-2xl">
          <AlertDescription>
            {sentInvites.length} of {totalInvites} invitations sent successfully
          </AlertDescription>
        </Alert>

        {/* Successful Invitations Table */}
        {hasSuccesses && (
          <div className="mb-6">
            <h3 className="mb-3 text-xl font-semibold text-success">Successful Invitations ({sentInvites.length})</h3>
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead>Email address</TableHead>
                  <TableHead>Full name</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sentInvites.map(invite => (
                  <TableRow key={`success-${invite.email}-${invite.name ?? 'name'}`}>
                    <TableCell>{invite.email}</TableCell>
                    <TableCell>{invite.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Failed Invitations Table */}
        {hasFailures && (
          <div className="mb-6">
            <h3 className="mb-3 text-xl font-semibold text-destructive">Failed Invitations ({failedInvites.length})</h3>
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead>Email address</TableHead>
                  <TableHead>Full name</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failedInvites.map((failed, index) => (
                  <TableRow key={`failed-${failed.invitation.email}-${index}`}>
                    <TableCell>{failed.invitation.email}</TableCell>
                    <TableCell>{failed.invitation.name}</TableCell>
                    <TableCell className="text-destructive">{failed.error}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button
            onClick={() => {
              props?.onDone?.();
              props?.onClose?.();
            }}
          >
            Done
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className={cn(props.className)}>
      {!showConfirmation ? renderInvitationForm() : renderConfirmationMessage()}
    </div>
  );
}

export default InviteTeammate;
